import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { LoaderCircle } from "lucide-react";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-pill px-6 text-button font-semibold ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:pointer-events-none [&_svg]:pointer-events-none [&_svg]:size-5 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-600",
        primary: "bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-600",
        "primary-inverse": "bg-white text-blue-600 hover:bg-blue-50 active:bg-blue-50",
        destructive: "bg-error text-white hover:bg-error/90",
        outline: "border border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-100 active:bg-neutral-100",
        secondary: "border border-neutral-200 bg-white text-neutral-900 hover:bg-neutral-100 active:bg-neutral-100",
        ghost: "bg-transparent text-blue-600 hover:bg-blue-50 active:bg-blue-50",
        link: "text-blue-600 underline-offset-4 hover:underline px-0",
        success: "bg-success text-white hover:bg-success/90",
        accent: "bg-blue-50 text-blue-700 hover:bg-blue-100",
      },
      size: {
        default: "h-[52px]",
        sm: "h-11 px-5 text-button-sm",
        lg: "h-[52px]",
        icon: "h-11 w-11 px-0",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, disabled, ...props }, ref) => {
    const isDisabledLook = Boolean(disabled) && !loading;
    const classes = cn(
      buttonVariants({ variant, size }),
      isDisabledLook &&
        (variant === "ghost" || variant === "link"
          ? "bg-transparent text-neutral-400 hover:bg-transparent"
          : "bg-neutral-100 text-neutral-400 hover:bg-neutral-100 active:bg-neutral-100"),
      className,
    );

    if (asChild) {
      return (
        <Slot className={classes} ref={ref} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button className={classes} ref={ref} disabled={disabled || loading} {...props}>
        {loading ? <LoaderCircle className="animate-spin" /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
